/**
 * Referrer-host normalisation for the funnel's landing step (TES-1858).
 *
 * ## Why this exists
 *
 * G1.4 (inbound referral velocity) had no instrument. `landing_view` already
 * carried a coarse `entry_source` (direct | organic | referral | internal), so
 * we could see *that* 7.8% of landings were referrals, but not *from where* —
 * and "which content bet paid off" is only answerable per-source. GA4 has no
 * API credentials, Vercel Analytics is off, and `funnel_events` dropped the
 * referrer entirely.
 *
 * ## Why the full hostname and not an eTLD+1
 *
 * A true registrable domain needs the Public Suffix List, which is a dependency
 * (and a stale-data liability) we don't want on the ingest path. The full
 * hostname is strictly more information and carries no PSL risk: the report can
 * always collapse `l.instagram.com` → Instagram, but it can't recover
 * `com.google.android.gm` (Gmail app) from `google.com`. The privacy line the
 * issue actually draws — hostname only, never a path or query string — is held
 * here: we read `URL.hostname` and nothing else.
 *
 * ## Trust boundary
 *
 * The client computes this value, so the ingest route re-runs
 * `normalizeReferrerHost` + `isValidReferrerHost` on whatever arrives rather
 * than trusting it. That is what keeps a hostile client from writing a path, a
 * query string, or a 2 KB blob into the `referrer_host` key.
 */

/** No referrer, or a same-origin referrer (a reload / an internal hard nav). */
export const REFERRER_HOST_DIRECT = 'direct'

/** A referrer we saw but could not resolve to a hostname (malformed URL). */
export const REFERRER_HOST_UNKNOWN = 'unknown'

/**
 * Hostnames are capped well under the 253-octet DNS limit. Anything longer is
 * not a real referrer, and the cap is what stops `properties` from growing.
 */
export const REFERRER_HOST_MAX_LENGTH = 128

/**
 * Conservative hostname shape: labels of `[a-z0-9-]` joined by dots. Rejects
 * anything carrying a slash, a `?`, a `#`, a colon (port / IPv6) or whitespace,
 * which is the whole point — those are the shapes that would smuggle a path or
 * a query string past the allow-list.
 */
const HOST_RE = /^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/

/**
 * Lowercases, drops the root-zone trailing dot, and folds `www.` away so
 * `www.reddit.com` and `reddit.com` are one bucket rather than two.
 *
 * Returns `''` for anything that isn't a usable hostname; callers decide
 * whether that means `direct` or `unknown`.
 */
export function normalizeReferrerHost(hostname: unknown): string {
  if (typeof hostname !== 'string') return ''
  let host = hostname.trim().toLowerCase()
  if (host.endsWith('.')) host = host.slice(0, -1)
  if (host.startsWith('www.')) host = host.slice(4)
  return host
}

/** True for a normalised hostname, or for the two sentinel buckets. */
export function isValidReferrerHost(value: unknown): value is string {
  if (typeof value !== 'string') return false
  if (value === REFERRER_HOST_DIRECT || value === REFERRER_HOST_UNKNOWN) return true
  if (value.length === 0 || value.length > REFERRER_HOST_MAX_LENGTH) return false
  return HOST_RE.test(value)
}

/**
 * Resolves `document.referrer` to the bucket we store.
 *
 * Same-origin collapses to `direct` rather than leaking the internal URL, per
 * the privacy posture the rest of the funnel already follows. Note that
 * `entry_source` keeps its separate `internal` label, so the two are still
 * distinguishable in a report that reads both keys.
 *
 * Non-http schemes are handled deliberately, not by accident: Android in-app
 * referrers arrive as `android-app://com.google.android.gm`, and WHATWG URL
 * parses that authority into `hostname`. Those are among the most useful
 * referrers we get, so they are kept rather than binned as unknown.
 */
export function referrerHostFromUrl(
  referrer: string | null | undefined,
  currentHostname: string | null | undefined,
): string {
  if (!referrer) return REFERRER_HOST_DIRECT

  let parsed: URL
  try {
    parsed = new URL(referrer)
  } catch {
    return REFERRER_HOST_UNKNOWN
  }

  const host = normalizeReferrerHost(parsed.hostname)
  if (!host) return REFERRER_HOST_UNKNOWN

  const current = normalizeReferrerHost(currentHostname)
  if (current && host === current) return REFERRER_HOST_DIRECT

  return isValidReferrerHost(host) ? host : REFERRER_HOST_UNKNOWN
}

/**
 * Server-side coercion for a client-supplied value.
 *
 * Never throws and never returns something outside the validated shape, so the
 * ingest route can drop the result straight into `properties`.
 */
export function coerceReferrerHost(value: unknown): string {
  const host = normalizeReferrerHost(value)
  if (!host) return REFERRER_HOST_UNKNOWN
  return isValidReferrerHost(host) ? host : REFERRER_HOST_UNKNOWN
}
